import Anthropic from '@anthropic-ai/sdk';
import type { CountryProfile, TroopMovement, MissileTrajectory, ConflictZone } from '@/types';
import type {
  SimulationScenario, SimulationTurn, SimMapEvent,
  SimMapEventMissile, SimMapEventTroopMove, SimMapEventConflictExpand,
} from '@/types/simulation';

// ---------------------------------------------------------------------------
// Anthropic client (browser-safe with explicit dangerouslyAllowBrowser)
// ---------------------------------------------------------------------------

function getClient(): Anthropic {
  const apiKey = import.meta.env.VITE_ANTHROPIC_API_KEY as string | undefined;
  if (!apiKey) throw new Error('VITE_ANTHROPIC_API_KEY is not set');
  return new Anthropic({ apiKey, dangerouslyAllowBrowser: true });
}

// ---------------------------------------------------------------------------
// Leader personality profiles (injected into system prompt)
// ---------------------------------------------------------------------------

const LEADER_PROFILES = `
LEADER PERSONALITY PROFILES (critical for realistic simulation):
- Kim Jong-un (North Korea): Nuclear gambler; regime survival above all; extreme risk tolerance; irrational escalation possible; responds to perceived weakness with aggression
- Donald Trump (USA): Transactional dealmaker; tariffs as leverage; unpredictable; responds to economic pain; media pressure sensitive; bilateral over multilateral
- Xi Jinping (China): Long-term strategist; Taiwan unification = historical mission; tolerates economic pain; wolf warrior diplomacy; no collective leadership
- Vladimir Putin (Russia): Historical revisionist; nuclear coercion doctrine; siege mentality; negotiations = time-buying; domestic narrative over international norms
- Benjamin Netanyahu (Israel): Existentialist; preemptive strike doctrine; Iran = existential threat; domestic coalition pressure; Begin Doctrine adherent
- Narendra Modi (India): Hindu nationalist; strategic autonomy; economic development priority; non-alignment modern; borders = red lines
- Recep Tayyip Erdoğan (Turkey): Political Islamism; neo-Ottoman regional power; NATO leverage player; Kurdish issue = existential
- Emmanuel Macron (France): European sovereignty advocate; strategic autonomy from US; multilateralism; nuclear deterrence independent
- Olaf Scholz (Germany): Consensus politician; economic interests; reluctant on defense; peace-through-trade legacy
- Fumio Kishida / successor (Japan): Constitutional constraints; US alliance dependent; China economic ties vs. security; Indo-Pacific framework
- Yoon Suk-yeol / successor (South Korea): Pro-US; hawkish on North Korea; trade dependence on China creates tension
`;

// ---------------------------------------------------------------------------
// Build system prompt
// ---------------------------------------------------------------------------

export function buildSystemPrompt(
  scenario: SimulationScenario,
  countryProfiles: CountryProfile[],
): string {
  // Serialize key country data compactly
  const profileSummaries = countryProfiles.slice(0, 18).map((c) => {
    const m = c.military;
    return `${c.name} (${c.code}): GDP $${c.gdpBillionUSD}B, Military budget $${m.budgetBillionUSD}B, ` +
      `Active troops ${m.activeTroops}k, Nuclear: ${c.nuclearStatus}, ` +
      `Alliances: ${c.alliances.slice(0, 4).join('/')}, ` +
      `Leader: ${c.leader.name} (risk: ${c.leader.riskLevel}), ` +
      `Stance: ${c.geopoliticalStance}`;
  }).join('\n');

  return `You are the GEOPOLITICAL SIMULATION ENGINE for DetectX Quasar — an advanced intelligence platform.
Your role is a neutral, expert Game Master simulating realistic geopolitical scenarios for educational and analytical purposes.

${LEADER_PROFILES}

COUNTRY DATA (real OSINT-verified, March 2026):
${profileSummaries}

SIMULATION PARAMETERS:
- Initiator: ${scenario.initiator}
- Target: ${scenario.target}
- Scenario Type: ${scenario.type}
- Intensity: ${scenario.intensity}/5
- Time Horizon: ${scenario.timeHorizon}
${scenario.additionalContext ? `- Additional Context: ${scenario.additionalContext}` : ''}

RULES:
1. Output ONLY valid JSON — no prose outside JSON
2. Be geopolitically realistic: use real military capabilities, alliances, economic dependencies
3. Leader personalities MUST affect decision-making (Kim Jong-un behaves differently from Xi Jinping)
4. Include SPECIFIC map_events with real coordinates — these drive live map animations
5. Provide exactly 3 choices (A/B/C) with different risk levels
6. Casualties and suffering treated with gravity — not glorified
7. All output labeled as SIMULATION

OUTPUT FORMAT (return ONLY this JSON):
{
  "turn_title": "string (e.g. Week 2: Escalation Begins)",
  "narrative": "2-3 rich paragraphs of geopolitical analysis",
  "events": [{"type": "military|diplomatic|economic|humanitarian|cyber", "description": "string"}],
  "map_events": [
    // missile: {"type":"missile","from_lon":number,"from_lat":number,"to_lon":number,"to_lat":number,"missile_type":"ballistic|cruise|hypersonic","fired_by":"string","target":"string","duration_ms":number},
    // carrier_group: {"type":"carrier_group","from_lon":number,"from_lat":number,"to_lon":number,"to_lat":number,"nation":"string","name":"string","ships":number},
    // troop_move: {"type":"troop_move","from_lon":number,"from_lat":number,"to_lon":number,"to_lat":number,"nation":"string","move_type":"ground|naval|air|logistics","scale":"company|battalion|brigade|division|corps","count":number},
    // fighter_sortie: {"type":"fighter_sortie","from_lon":number,"from_lat":number,"to_lon":number,"to_lat":number,"nation":"string","count":number},
    // conflict_expand: {"type":"conflict_expand","lat":number,"lon":number,"radius_km":number,"intensity":number,"nation":"string"},
    // submarine: {"type":"submarine","from_lon":number,"from_lat":number,"to_lon":number,"to_lat":number,"nation":"string"},
    // drone_swarm: {"type":"drone_swarm","from_lon":number,"from_lat":number,"to_lon":number,"to_lat":number,"drone_type":"shahed|fpv|loitering|surveillance","fired_by":"string","count":number},
    // nuclear_alert: {"type":"nuclear_alert","lat":number,"lon":number,"nation":"string","alert_level":"elevated|launch_ready|launched"},
    // sanctions_ring: {"type":"sanctions_ring","nation":"string","severity":number,"trade_routes_blocked":["string"]},
    // economic_zone: {"type":"economic_zone","nation":"string","impact_pct":number,"affected_sectors":["string"]}
  ],
  "country_statuses": [{"code":"ISO2","status":"string","alert":"string","leader_action":"string"}],
  "impacts": {
    "global_gdp_pct": number,
    "country_gdp": [{"code":"string","pct":number}],
    "nuclear_risk": integer 0-100 (0=no nuclear risk, 50=credible threat, 80=launch authorized, 95=launch detected, 100=detonation occurred),
    "casualties": "string",
    "countries_involved": ["ISO2 codes"],
    "market_snapshot": "string",
    "supply_chain_hit": ["string"]
  },
  "leader_assessments": [{"leader":"string","assessment":"string"}],
  "choices": [
    {"id":"A","label":"string","consequence":"string","risk_level":"low|medium|high|critical"},
    {"id":"B","label":"string","consequence":"string","risk_level":"low|medium|high|critical"},
    {"id":"C","label":"string","consequence":"string","risk_level":"low|medium|high|critical"}
  ],
  "resolution_probability": number,
  "next_turn_preview": "string",
  "sim_note": "SIMULATION — Educational geopolitical analysis only"
}`;
}

// ---------------------------------------------------------------------------
// Build user message for each turn
// ---------------------------------------------------------------------------

function buildTurnMessage(
  scenario: SimulationScenario,
  history: SimulationTurn[],
  choiceId?: 'A' | 'B' | 'C',
): string {
  if (history.length === 0) {
    return `TURN 1 — INITIAL STATE\nSimulate the opening of this scenario. Set the stage for what's happening.`;
  }

  const lastTurn = history[history.length - 1];
  const chosen = choiceId
    ? lastTurn.choices.find((c) => c.id === choiceId)
    : lastTurn.choices[0]; // auto mode: pick first (least escalatory)

  return `TURN ${history.length + 1}\n` +
    `Previous turn: "${lastTurn.turn_title}"\n` +
    `Resolution probability: ${lastTurn.resolution_probability}%\n` +
    `Choice made: [${choiceId ?? 'A'}] ${chosen?.label ?? 'N/A'}\n` +
    `Continue the simulation. Escalate or de-escalate based on the choice and leader personalities.`;
}

// ---------------------------------------------------------------------------
// Run a simulation turn (streaming)
// ---------------------------------------------------------------------------

export async function runSimulationTurn(
  scenario: SimulationScenario,
  countryProfiles: CountryProfile[],
  history: SimulationTurn[],
  choiceId: 'A' | 'B' | 'C' | undefined,
  onChunk: (chunk: string) => void,
): Promise<SimulationTurn> {
  const client = getClient();
  const systemPrompt = buildSystemPrompt(scenario, countryProfiles);
  const userMessage = buildTurnMessage(scenario, history, choiceId);

  // Build message history for context
  const messages: Anthropic.MessageParam[] = [];
  for (const turn of history) {
    messages.push({ role: 'user', content: `TURN ${turn.turnNumber} REQUEST` });
    messages.push({ role: 'assistant', content: JSON.stringify(turn) });
  }
  messages.push({ role: 'user', content: userMessage });

  let fullText = '';

  const stream = await client.messages.stream({
    model: 'claude-opus-4-6',
    max_tokens: 4096,
    system: systemPrompt,
    messages,
  });

  for await (const event of stream) {
    if (
      event.type === 'content_block_delta' &&
      event.delta.type === 'text_delta'
    ) {
      fullText += event.delta.text;
      onChunk(event.delta.text);
    }
  }

  return parseSimulationResponse(fullText, history.length + 1);
}

// ---------------------------------------------------------------------------
// Parse Claude response → SimulationTurn
// ---------------------------------------------------------------------------

export function parseSimulationResponse(raw: string, turnNumber: number): SimulationTurn {
  // Extract JSON — try multiple strategies from most to least specific
  let jsonStr: string | null = null;

  // Strategy 1: code fence with json tag (most common Claude output)
  const fenceJson = raw.match(/```json\s*(\{[\s\S]*\})\s*```/);
  if (fenceJson) jsonStr = fenceJson[1];

  // Strategy 2: any code fence
  if (!jsonStr) {
    const fenceAny = raw.match(/```\s*(\{[\s\S]*\})\s*```/);
    if (fenceAny) jsonStr = fenceAny[1];
  }

  // Strategy 3: find outermost { ... } in the raw text
  if (!jsonStr) {
    const start = raw.indexOf('{');
    const end = raw.lastIndexOf('}');
    if (start !== -1 && end > start) jsonStr = raw.slice(start, end + 1);
  }

  let parsed: Record<string, unknown>;
  try {
    parsed = JSON.parse(jsonStr ?? raw.trim()) as Record<string, unknown>;
  } catch {
    // JSON parse failed — return stub with error narrative (never show raw JSON)
    console.warn('[Simulation] JSON parse failed, raw length:', raw.length, raw.slice(0, 200));
    return {
      turnNumber,
      turn_title: `Turn ${turnNumber} — Analysis Pending`,
      narrative: 'Intelligence analysis could not be parsed. The simulation engine encountered an unexpected response format. Please advance to the next turn.',
      events: [],
      map_events: [],
      country_statuses: [],
      impacts: {
        global_gdp_pct: 0,
        country_gdp: [],
        nuclear_risk: 0,
        casualties: 'Assessing...',
        countries_involved: [],
        market_snapshot: 'Data unavailable',
      },
      leader_assessments: [],
      choices: [
        { id: 'A', label: 'Continue current strategy', consequence: 'Status quo maintained', risk_level: 'low' },
        { id: 'B', label: 'Escalate pressure', consequence: 'Increased geopolitical tension', risk_level: 'medium' },
        { id: 'C', label: 'Seek diplomatic channel', consequence: 'Possible de-escalation', risk_level: 'low' },
      ],
      resolution_probability: 50,
    };
  }

  return {
    turnNumber,
    turn_title: (parsed.turn_title as string) ?? `Turn ${turnNumber}`,
    narrative: (parsed.narrative as string) ?? '',
    events: (parsed.events as SimulationTurn['events']) ?? [],
    map_events: (parsed.map_events as SimMapEvent[]) ?? [],
    country_statuses: (parsed.country_statuses as SimulationTurn['country_statuses']) ?? [],
    impacts: (parsed.impacts as SimulationTurn['impacts']) ?? {
      global_gdp_pct: 0, country_gdp: [], nuclear_risk: 0,
      casualties: 'N/A', countries_involved: [], market_snapshot: 'N/A',
    },
    leader_assessments: (parsed.leader_assessments as SimulationTurn['leader_assessments']) ?? [],
    choices: (parsed.choices as SimulationTurn['choices']) ?? [],
    resolution_probability: (parsed.resolution_probability as number) ?? 50,
    next_turn_preview: parsed.next_turn_preview as string | undefined,
  };
}

// ---------------------------------------------------------------------------
// Convert map_events → store-compatible layer data
// ---------------------------------------------------------------------------

export interface SimLayerData {
  missiles: MissileTrajectory[];
  troopMovements: TroopMovement[];
  conflictZones: ConflictZone[];
}

export function mapEventsToLayers(events: SimMapEvent[], turnNumber: number): SimLayerData {
  const missiles: MissileTrajectory[] = [];
  const troopMovements: TroopMovement[] = [];
  const conflictZones: ConflictZone[] = [];

  const now = Date.now();

  events.forEach((ev, idx) => {
    const id = `sim-${turnNumber}-${idx}`;

    if (ev.type === 'missile') {
      const m = ev as SimMapEventMissile;
      const duration = m.duration_ms ?? 12000;
      missiles.push({
        id,
        type: m.missile_type ?? 'ballistic',
        originLon: m.from_lon,
        originLat: m.from_lat,
        targetLon: m.to_lon,
        targetLat: m.to_lat,
        firedBy: m.fired_by,
        targetName: m.target,
        flightTimeMs: duration,
        startedAt: now,
        status: 'flying',
        progress: 0,
        currentLon: m.from_lon,
        currentLat: m.from_lat,
        relatedNews: [],
      });
    }

    if (ev.type === 'fighter_sortie' || ev.type === 'troop_move' || ev.type === 'carrier_group' || ev.type === 'submarine') {
      const m = ev as SimMapEventTroopMove;
      const moveType = ev.type === 'fighter_sortie' ? 'air'
        : ev.type === 'carrier_group' || ev.type === 'submarine' ? 'naval'
        : m.move_type ?? 'ground';
      const scale = m.scale ?? (ev.type === 'carrier_group' ? 'division' : 'brigade');
      troopMovements.push({
        id,
        fromCountry: m.nation ?? 'Unknown',
        fromLon: m.from_lon,
        fromLat: m.from_lat,
        toCountry: 'Target',
        toLon: m.to_lon,
        toLat: m.to_lat,
        type: moveType as TroopMovement['type'],
        scale: scale as TroopMovement['scale'],
        description: `[SIM] ${m.nation ?? ''} ${ev.type.replace('_', ' ')}`,
        relatedNews: [],
        startDate: new Date().toISOString(),
        severity: 'critical',
      });
    }

    if (ev.type === 'conflict_expand') {
      const c = ev as SimMapEventConflictExpand;
      conflictZones.push({
        id,
        name: `[SIM] ${c.nation ?? 'Conflict Zone'}`,
        description: `Simulation conflict expansion`,
        lat: c.lat,
        lon: c.lon,
        radiusKm: c.radius_km ?? 200,
        severity: c.intensity > 0.7 ? 'critical' : c.intensity > 0.4 ? 'high' : 'medium',
        parties: [c.nation ?? 'Unknown'],
        startDate: new Date().toISOString(),
        type: 'active-war',
      });
    }
  });

  return { missiles, troopMovements, conflictZones };
}
